import * as React from "react"

import type { Row } from "@tanstack/react-table"
import { useVirtualizer, type VirtualItem } from "@tanstack/react-virtual"

import { cn } from "../../cn"
import { Skeleton } from "../Skeleton/Skeleton"
import { buildPlatterSections, type PlatterSection } from "./platterSections"
import {
  ActionsHeaderCell,
  HeaderCell,
  SelectAllHeaderCell,
} from "./TableChrome"
import { TABLE_FRAME_SURFACE_CLASS } from "./tableFrame"
import {
  ACTION_COLUMN_WIDTH,
  columnAlign,
  columnLabel,
  EDGE_GUTTER_CLASS,
  EDGE_GUTTER_PX,
  EDGE_TO_EDGE_RING_ROOM_PX,
  EMPTY_SELECTION,
  ESTIMATED_ROW_HEIGHT,
  FLUSH_EDGE_CELLS_CLASS,
  LOAD_MORE_ROW_THRESHOLD,
  OVERSCAN,
  ROW_CONTROL_WORDS,
  rowLabelColumnId,
  SELECT_COLUMN_WIDTH,
  SELECT_COLUMN_WIDTH_CARD,
  shouldOpenRowContextMenu,
  type PinMeta,
} from "./TableInternals"
import { BodyRow } from "./TableRows"
import { FullWidthRow, LoadingRows } from "./TableStateRows"
import type { SelectionModifiers, TableProps } from "./types"
import { usePlatterScrollSync } from "./usePlatterScrollSync"
import { useTableController } from "./useTableController"

// Default left offset (px per nesting depth) for `cardRows` platters: aligns a
// nested card's left edge with the group row's icon (past the chevron `size-4`
// + `gap-1.5`). Consumers can override per-table via the `cardRowNestLead` prop
// — e.g. the metrics tables zero it so the platter's left edge sits flush with
// the group row's chevron, on the same margin as the rest of the page.
const CARD_NEST_ICON_LEAD = 16 + 6

export function Table<T>(props: TableProps<T>) {
  const {
    data,
    columns,
    getRowId,
    onRowClick,
    isRowClickable,
    rowClickDescription,
    getRowLabel,
    rowHover = true,
    activeRowId,
    onRowPointerEnter,
    onRowPointerLeave,
    isLoading,
    isFetchingMore,
    hasMore,
    onLoadMore,
    onSortChange,
    defaultSort,
    sortStorageKey,
    selectable,
    selectedIds,
    onSelectionChange,
    isRowSelectable,
    isRowSelectionDisabled,
    showSelectAll,
    allowRangeSelection,
    selectionVisibility,
    rowClickSelects,
    columnVisibility,
    onColumnVisibilityChange,
    columnOrder,
    onColumnOrderChange,
    headerAction,
    rowActions,
    rowCopyId,
    rowHref,
    onRowOpenInNewTab,
    getSubRows,
    expandedIds,
    onExpandedChange,
    defaultExpanded,
    renderGroupRow,
    isColumnHeaderRow,
    initialScrollOffset,
    onScrollChange,
    scrollRef: scrollRefProp,
    estimateRowHeight,
    measureRow,
    measuredRowsKey,
    rowAttributes,
    rowVariant,
    cellVariant,
    isRowUnread,
    emptyState,
    edgeToEdge,
    framed,
    cardRows,
    cardRowNestLead,
    className,
    ...rest
  } = props

  // Per-depth left offset for `cardRows` platters. Defaults to aligning the card
  // edge under the group icon; a consumer can zero it (e.g. the metrics tables)
  // to sit the platter edge flush with the group row's chevron.
  const cardNestLead = cardRowNestLead ?? CARD_NEST_ICON_LEAD

  // Resolved outer-gutter width. `edgeToEdge` collapses it for tables embedded
  // in an already-padded container — down to the 2px a focus ring needs past a
  // control on the table's edge, which the scroll container would otherwise
  // clip. The container gives those 2px back by reaching out past its box as
  // far (see `ringRoom` below), so content still lines up with the container's
  // own padding. Platter rows keep their own inset and collapse it to zero.
  // Drives the `--table-edge-gutter` CSS variable (padding + gutter covers) and
  // the numeric sticky offsets below.
  const ringRoom = Boolean(edgeToEdge) && !cardRows
  const edgeGutterPx = edgeToEdge
    ? ringRoom
      ? EDGE_TO_EDGE_RING_ROOM_PX
      : 0
    : EDGE_GUTTER_PX

  const ariaLabel = "aria-label" in rest ? rest["aria-label"] : undefined
  const ariaLabelledby =
    "aria-labelledby" in rest ? rest["aria-labelledby"] : undefined

  const { columnsById, nestable, table } = useTableController({
    columnOrder,
    columns,
    columnVisibility,
    data,
    defaultExpanded,
    defaultSort,
    expandedIds,
    getRowId,
    getSubRows,
    onColumnOrderChange,
    onColumnVisibilityChange,
    onExpandedChange,
    onSortChange,
    sortStorageKey,
    ariaLabel: ariaLabel ?? ariaLabelledby,
  })

  // Selection. Fully controlled by the consumer via `selectedIds` /
  // `onSelectionChange`; the table only derives header tri-state and toggles.
  // "Select all" operates on the currently loaded rows — with infinite scroll
  // that's the loaded page, not the unfetched remainder (see the scope helpers
  // below, which read the flat row model rather than `data`).
  const selectedSet = selectedIds ?? EMPTY_SELECTION

  // The row a shift-click extends from: the last row picked by a plain click,
  // or the far end of the last range. A ref rather than state because nothing
  // renders from it — it only reads back on the next click — and because a
  // re-render between the two clicks must not reset it.
  const selectionAnchorRef = React.useRef<string | null>(null)

  // The visible leaf columns are the single source of truth for layout: header,
  // body, and the grid track list all derive from them, so visibility, order,
  // and live resize stay in lockstep.
  const leafColumns = table.getVisibleLeafColumns()

  // Column pinning. Pinned columns stick to the left/right edge during
  // horizontal scroll; the pinning groups override the column order — left-
  // pinned first, center next, right-pinned last — with relative order within
  // each group preserved.
  const leftPinned = leafColumns.filter(
    (leaf) => columnsById.get(leaf.id)?.pinned === "left",
  )
  const rightPinned = leafColumns.filter(
    (leaf) => columnsById.get(leaf.id)?.pinned === "right",
  )
  const centerColumns = leafColumns.filter(
    (leaf) => !columnsById.get(leaf.id)?.pinned,
  )
  // Single ordered list that header, body, and grid tracks all iterate, so the
  // pinned-grouped order stays in lockstep across the three.
  const displayColumns = [...leftPinned, ...centerColumns, ...rightPinned]
  // The nesting expand/collapse control and depth indent live in the first
  // displayed column.
  const firstColumnId = displayColumns[0]?.id

  // Row width strategy. A row must grow past the viewport when it overflows so
  // its background / bottom border span the full scrollable width and its sticky
  // pinned cells clamp to the same box the body rows do (a viewport-wide row
  // clamps them out of line during horizontal scroll). Body rows are
  // `position: absolute`, so `min-w-full` already shrink-wraps them to content;
  // the in-flow header/loading rows need an explicit content-width to match.
  //
  // `w-max` sizes the grid to max-content, under which CSS grid resolves `fr`
  // (center) tracks to their content's max-content: a long unbreakable value
  // (e.g. a `sip:` URI) — or even just a wide header label — balloons its track
  // and shoves later columns off-screen instead of truncating. So `w-max` is
  // safe only when no `fr` tracks exist (every column fixed/pinned). With center
  // columns present we use `w-fit` (fit-content): it grows the box to the
  // content width like the absolute body rows, but resolves `fr` under the
  // viewport so tracks stay at their floor (reflowing to fill when there's no
  // overflow) and stay aligned with the body, and overflow-hidden
  // cells truncate.
  const rowWidthClass =
    centerColumns.length === 0 ? "min-w-full w-max" : "min-w-full w-fit"

  // A pinned column is always the layer everything else passes beneath: it is
  // stuck to its edge, opaque, and above the center cells, so content that
  // reaches it goes under it rather than across it. That has to hold whether or
  // not the table currently overflows — a center cell can reach a pinned column
  // by overflowing its own track, not only by being scrolled under. What the
  // overflow measurement gates is the *chrome* that only makes sense once there
  // is something to scroll: the edge divider (`scrolledX` / `canScrollRight`).
  //
  // Overflow is still measured from the DOM (see the effect below) for that
  // chrome. Column widths and order don't depend on it (see `dataTracks`), so
  // the measurement can't feed back into itself.
  // The copy group is enough on its own to give a table a row menu, so the
  // trailing track is reserved for it too.
  const hasActions =
    Boolean(headerAction) ||
    Boolean(rowActions) ||
    Boolean(rowCopyId) ||
    Boolean(rowHref)

  // Sticky offsets, recomputed every render so they track a live resize. Left
  // offsets start after the selection column (which then sticks alongside the
  // pinned block); right offsets accumulate from the far edge inward.
  const selectColumnWidth =
    (cardRows ? SELECT_COLUMN_WIDTH_CARD : SELECT_COLUMN_WIDTH) + edgeGutterPx
  const pinnedById = new Map<string, PinMeta>()
  {
    let leftOffset = (selectable ? selectColumnWidth : 0) + edgeGutterPx
    leftPinned.forEach((leaf, index) => {
      pinnedById.set(leaf.id, {
        side: "left",
        offset: leftOffset,
        edge: index === leftPinned.length - 1,
        // The selection column, when present, is the outermost left sticky cell
        // and paints the gutter cover itself, so the first pinned column doesn't.
        outer: !selectable && index === 0,
      })
      leftOffset += leaf.getSize()
    })
    // The trailing actions column is the outermost right-pinned track, so the
    // right-pinned data columns start after its width.
    let rightOffset = (hasActions ? ACTION_COLUMN_WIDTH : 0) + edgeGutterPx
    for (let index = rightPinned.length - 1; index >= 0; index--) {
      const leaf = rightPinned[index]
      pinnedById.set(leaf.id, {
        side: "right",
        offset: rightOffset,
        // Explicit end lines keep the right-side cells flush right, past the
        // spacer track that can sit between them and the center columns.
        gridColumnEnd:
          -1 - (hasActions ? 1 : 0) - (rightPinned.length - 1 - index),
        edge: index === 0,
        // The actions column, when present, is the outermost right sticky cell
        // and paints the gutter cover itself, so the last pinned column doesn't.
        outer: !hasActions && index === rightPinned.length - 1,
      })
      rightOffset += leaf.getSize()
    }
  }
  // The selection checkbox column sticks to the left edge so rows can't scroll
  // away from their own checkboxes; it's the outermost left sticky cell and owns
  // the left gutter cover.
  const selectSticky = Boolean(selectable)

  // Pinned columns render as fixed `size`px tracks (sticky offsets need pixel
  // widths); undragged center columns keep `minmax(minWidth, size fr)` so their
  // `size` is shared out proportionally and the columns fill the table — the
  // `fr` basis preserves responsive reflow when the Sofia panel docks. Once every
  // column is at its floor the table scrolls (overflow-auto on the root). A leading fixed-
  // width track renders the checkbox column when `selectable`.
  // A column the user has dragged renders at exactly its dragged width, so its
  // edge follows the pointer in both directions. Untouched `fr` columns absorb
  // the leftover space; once none remain, an empty spacer track does. It sits
  // after the center columns, before the right-pinned and actions columns,
  // which are placed on explicit end lines so no cell is auto-placed into it.
  // It collapses to zero when the table overflows.
  const columnSizing = table.getState().columnSizing
  const flexCenterColumns = centerColumns.filter(
    (leaf) => columnsById.get(leaf.id)?.flex !== false,
  )
  const needsSpacerTrack =
    flexCenterColumns.length > 0 &&
    flexCenterColumns.every((leaf) => leaf.id in columnSizing)
  const dataTracks = displayColumns.map((leaf) => {
    const size = leaf.getSize()
    const col = columnsById.get(leaf.id)
    // Pinned columns need pixel widths for their sticky offsets; a column with
    // `flex: false` opts out of `fr` reflow so it stays exactly `size`px (its
    // width is content-derived and shouldn't stretch to fill leftover space).
    // Keyed off the declared `pinned` (not `pinnedById`, which is gated on
    // overflow) so column widths and thus `scrollWidth` stay stable regardless
    // of whether pinning is active — the overflow measurement can't oscillate.
    if (col?.pinned != null || col?.flex === false) {
      return `${size}px`
    }
    if (leaf.id in columnSizing) {
      return `${size}px`
    }
    const minWidth = col?.minWidth ?? size
    return `minmax(${minWidth}px, ${size}fr)`
  })
  const leadingTrackCount = leftPinned.length + centerColumns.length
  const gridTemplateColumns = [
    ...(selectable ? [`${selectColumnWidth}px`] : []),
    ...dataTracks.slice(0, leadingTrackCount),
    ...(needsSpacerTrack ? ["minmax(0px, 1fr)"] : []),
    ...dataTracks.slice(leadingTrackCount),
    ...(hasActions ? [`${ACTION_COLUMN_WIDTH}px`] : []),
  ].join(" ")

  // Lookup so the header row can render in `displayColumns` order (TanStack's
  // `headerGroup.headers` keeps the unpinned column order).
  const leafHeaderById = new Map(
    table.getLeafHeaders().map((header) => [header.column.id, header]),
  )

  // A table still fetching its next page is not empty yet; the fetching-more
  // row stands in for the empty state until the page settles.
  const showEmpty = !isLoading && !isFetchingMore && data.length === 0
  const rows = table.getRowModel().rows

  // Every grid track, for the one cell that spans a group band or a state row.
  // At least one, so a table whose columns are all hidden still spans a track.
  const columnCount = Math.max(
    1,
    (selectable ? 1 : 0) + displayColumns.length + (hasActions ? 1 : 0),
  )
  // The virtualizer renders a window of the rows, so the table reports its full
  // count and each rendered row its 1-based place in it, header rows included.
  // A table that can still load more doesn't know its total and says so (-1).
  const headerRowCount = isColumnHeaderRow ? 0 : table.getHeaderGroups().length
  // While loading, the only row assistive tech finds is the loading notice.
  const ariaRowCount = isLoading
    ? headerRowCount + 1
    : hasMore
      ? -1
      : headerRowCount +
        (showEmpty ? 1 : rows.length) +
        (isFetchingMore ? 1 : 0)
  const clickDescriptionId = React.useId()
  const controlNamesId = React.useId()
  const labelColumnId = getRowLabel
    ? undefined
    : rowLabelColumnId(displayColumns.map((leaf) => columnsById.get(leaf.id)))

  // Rows a "select all" covers, out of a given slice of the row model: the ones
  // that carry a checkbox at all. Group bands and stand-in column-header rows
  // render no checkbox, and `isRowSelectable` gates the rest, so none of them
  // may enter the selection set. Reading the row model (not `data`) is what lets
  // a grouped table select its sections' rows, which are sub-rows of the bands.
  //
  // `togglableIds` narrows that to the rows the select-all may actually flip:
  // `isRowSelectionDisabled` blocks a row that is selectable in principle but
  // not right now. Blocked rows stay in `ids` so the tri-state counts them,
  // which is what stops a header from reading fully checked once its toggleable
  // scope has shrunk to the rows already selected.
  const selectionScopeIn = (scope: Row<T>[]) => {
    const eligible = scope.filter(
      (row) =>
        !isColumnHeaderRow?.(row.original) &&
        renderGroupRow?.(row.original) == null &&
        (!isRowSelectable || isRowSelectable(row.original)),
    )
    return {
      ids: eligible.map((row) => row.id),
      togglableIds: eligible
        .filter((row) => !isRowSelectionDisabled?.(row.original))
        .map((row) => row.id),
    }
  }

  // Tri-state for one scope: checked when every row in it is selected,
  // indeterminate when only some are.
  const selectionStateOf = (ids: string[]) => {
    const all = ids.length > 0 && ids.every((id) => selectedSet.has(id))
    return {
      allSelected: all,
      someSelected: !all && ids.some((id) => selectedSet.has(id)),
    }
  }

  const toggleIds = (ids: string[], allSelected: boolean) => {
    if (!onSelectionChange) {
      return
    }
    const next = new Set(selectedSet)
    ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)))
    onSelectionChange(next)
  }

  // Row checkbox toggle. A plain click flips its own row and becomes the anchor
  // for the next one; a shift-click extends from that anchor, giving every row
  // between the two ends the state the clicked row is moving into — so the
  // gesture that selects a range also clears one, and repeating it is
  // reversible. The range spans the rows in display order, skipping the ones no
  // checkbox may flip (group bands, stand-in column headers, `isRowSelectable`
  // rejects) and the ones blocked right now (`isRowSelectionDisabled`), so it
  // never selects a row the consumer said it could not.
  //
  // Without a usable anchor — the first click of a session, or an anchor whose
  // row has since been filtered, sorted, or paged out of the row model — the
  // shift is ignored and the click reads as a plain toggle. So is a table that
  // opted out via `allowRangeSelection={false}`, whose consumer keeps a single
  // choice and has nowhere to put the rest of a range.
  const toggleRow = (id: string, modifiers: SelectionModifiers) => {
    if (!onSelectionChange) {
      return
    }
    const anchorId = selectionAnchorRef.current
    selectionAnchorRef.current = id

    // The clicked checkbox's own destination drives the whole range.
    const selecting = !selectedSet.has(id)
    const next = new Set(selectedSet)
    const apply = (rowId: string) =>
      selecting ? next.add(rowId) : next.delete(rowId)

    const rangeIds =
      modifiers.shiftKey &&
      allowRangeSelection !== false &&
      anchorId !== null &&
      anchorId !== id
        ? selectionRangeBetween(anchorId, id)
        : null
    if (rangeIds) {
      rangeIds.forEach(apply)
    } else {
      apply(id)
    }
    onSelectionChange(next)
  }

  // The togglable rows from one end of a shift-click to the other, inclusive, in
  // display order. `null` when either end is no longer in the row model, which
  // is the caller's cue to fall back to a plain toggle rather than guess at a
  // range. Indices come from the full eligible list so a blocked row still
  // spans the gap it occupies; only togglable ids come back out.
  function selectionRangeBetween(anchorId: string, id: string) {
    const { ids, togglableIds } = selectionScopeIn(rows)
    const anchorIndex = ids.indexOf(anchorId)
    const clickedIndex = ids.indexOf(id)
    if (anchorIndex === -1 || clickedIndex === -1) {
      return null
    }
    const togglable = new Set(togglableIds)
    return ids
      .slice(
        Math.min(anchorIndex, clickedIndex),
        Math.max(anchorIndex, clickedIndex) + 1,
      )
      .filter((rowId) => togglable.has(rowId))
  }

  // The root is the scroll container; the virtualizer reads its scroll offset.
  // Virtualization only kicks in when the consumer constrains the root height
  // (via `className` or a flex parent); otherwise every row renders.
  const scrollRef = React.useRef<HTMLDivElement>(null)
  // Publish the scroll container to a consumer that asked for it, so the root
  // keeps the single ref the virtualizer reads.
  React.useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(
    scrollRefProp,
    () => scrollRef.current,
    [],
  )
  // Horizontal scroll state driving the pinned-column edge dividers. `scrolledX`
  // (scrollLeft > 0) means center content sits under the LEFT-pinned columns;
  // `canScrollRight` (content still hidden past the right edge) means it sits
  // under the RIGHT-pinned columns — true even at rest, since an overflowing
  // table hides its right side under the right-pinned block from the start.
  const [scrolledX, setScrolledX] = React.useState(false)
  const [canScrollRight, setCanScrollRight] = React.useState(false)
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    // Key measurements by row id so cached heights follow a row across data
    // appends (keyset pagination) rather than sticking to an index.
    getItemKey: (index) => rows[index]?.id ?? index,
    estimateSize: estimateRowHeight
      ? (index) => {
          const row = rows[index]
          return row
            ? estimateRowHeight(row.original, index)
            : ESTIMATED_ROW_HEIGHT
        }
      : () => ESTIMATED_ROW_HEIGHT,
    overscan: OVERSCAN,
  })
  const virtualItems = virtualizer.getVirtualItems()

  // Opt-in measured rows (see `measureRow`). Only rows that opt in attach the
  // ResizeObserver-backed `measureElement` ref; the rest keep the estimate-only
  // fast path. When the set of measured rows changes (a row expands/collapses),
  // reset the virtualizer's cached measurements so a collapsed row doesn't keep
  // its former expanded height. Scoped to `measureRow` consumers — a fixed-
  // height table never runs this.
  //
  // Prefer the consumer-supplied `measuredRowsKey` when present: it already
  // identifies the measured set, so we avoid an O(n) scan of every loaded row on
  // each render (which would otherwise run on every infinite-pagination append,
  // not just on expand/collapse). Fall back to scanning `rows` when no key is
  // given.
  const measuredSignature = React.useMemo(
    () =>
      measuredRowsKey ??
      (measureRow
        ? rows
            .filter((row) => measureRow(row.original))
            .map((row) => row.id)
            .join(",")
        : ""),
    [measuredRowsKey, rows, measureRow],
  )
  const prevMeasuredSignatureRef = React.useRef(measuredSignature)
  React.useEffect(() => {
    if (prevMeasuredSignatureRef.current !== measuredSignature) {
      prevMeasuredSignatureRef.current = measuredSignature
      virtualizer.measure()
    }
  }, [measuredSignature, virtualizer])

  // Scroll restoration. Apply `initialScrollOffset` once on mount (after layout,
  // via rAF) so a consumer can restore the position from before a navigation.
  const didRestoreScrollRef = React.useRef(false)
  React.useEffect(() => {
    if (didRestoreScrollRef.current || initialScrollOffset == null) {
      return
    }
    const el = scrollRef.current
    // Wait for rows before restoring: scrolling an empty / skeleton container
    // clamps the offset to ~0, and it's lost once the real rows grow the body.
    if (!el || rows.length === 0) {
      return
    }
    didRestoreScrollRef.current = true
    const frame = requestAnimationFrame(() => {
      el.scrollTo({ top: initialScrollOffset, behavior: "instant" })
    })
    return () => cancelAnimationFrame(frame)
  }, [initialScrollOffset, rows.length])

  // Infinite scroll. We check actual scroll position rather than the rendered
  // virtual indices because the high overscan renders most rows up front, which
  // would otherwise fire a cascade of fetches on first paint (per VirtualRunsTable).
  React.useEffect(() => {
    const el = scrollRef.current
    if (!el || !onLoadMore || !hasMore || isFetchingMore) {
      return
    }
    const distanceFromBottom =
      virtualizer.getTotalSize() - el.scrollTop - el.clientHeight
    if (distanceFromBottom < LOAD_MORE_ROW_THRESHOLD * ESTIMATED_ROW_HEIGHT) {
      onLoadMore()
    }
  }, [virtualItems, onLoadMore, hasMore, isFetchingMore, virtualizer])

  // Keep the active row scrolled into view. The virtualizer owns scrolling
  // because a row outside the rendered window isn't in the DOM for an external
  // scroll-into-view; `align: "auto"` only moves when the row is off-screen, so
  // hovering an already-visible row (pointer-driven active changes) is a no-op.
  React.useEffect(() => {
    if (!activeRowId) {
      return
    }
    const index = rows.findIndex((row) => row.id === activeRowId)
    if (index >= 0) {
      virtualizer.scrollToIndex(index, { align: "auto" })
    }
  }, [activeRowId, rows, virtualizer])

  // Platter mode scrolls horizontally inside each platter rather than in the
  // root, so the horizontal state (and the overflow that gates pinning) comes
  // from the platters, kept at one shared offset.
  const { registerPlatter, handlePlatterScroll, remeasurePlatters } =
    usePlatterScrollSync({
      onScrollStateChange: (state) => {
        setScrolledX(state.scrolledX)
        setCanScrollRight(state.canScrollRight)
      },
    })

  // Measure horizontal overflow so a pinned edge only draws its divider once
  // there is content under it. Re-measured whenever the layout could change
  // (column set / sizes / row count) and on container resize.
  React.useEffect(() => {
    const el = scrollRef.current
    if (!el) {
      return
    }
    const measure = () => {
      if (cardRows) {
        remeasurePlatters()
        return
      }
      setCanScrollRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 1)
    }
    measure()
    if (typeof ResizeObserver === "undefined") {
      return
    }
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [gridTemplateColumns, data.length, cardRows, remeasurePlatters])

  const handleScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const el = event.currentTarget
      onScrollChange?.(el.scrollTop)
      // Platter mode: the root only scrolls vertically — reading its (zero)
      // horizontal overflow here would disengage the platters' pinned columns.
      if (cardRows) {
        return
      }
      setScrolledX(el.scrollLeft > 0)
      setCanScrollRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 1)
    },
    [onScrollChange, cardRows],
  )

  // The selection column's right edge draws its divider only while pinned and
  // scrolled under.
  const selectDivider = selectSticky && scrolledX
  // The trailing actions column only touches scrolling content on its inner edge
  // when it is the innermost right-pinned cell (no right-pinned data column sits
  // between it and the center) — then it draws a divider like any pinned edge.
  const actionsEdgeDivider = rightPinned.length === 0 && canScrollRight

  // Per-section column headers: the consumer marks stand-in rows via
  // `isColumnHeaderRow` and injects them where headers should repeat. Providing
  // the predicate suppresses the single sticky header at the top of the table.
  const perSectionHeaders = Boolean(isColumnHeaderRow)

  // The header cells (select-all, sortable column headers, actions spacer) as a
  // fresh fragment on each call — shared by the global header row and, when
  // `perSectionHeaders`, each injected section-header row, so both stay in
  // lockstep with sort state, order, pinning, and resize. A function (not a
  // memoized node) so keys stay valid across the multiple parents it renders in.
  //
  // `headerRow` is the stand-in row a per-section header renders for. Its
  // select-all covers that section alone — the rows sharing its parent — so a
  // section's checkbox reads and toggles what sits under it rather than the
  // whole table. The global header passes none and covers every loaded row.
  const renderHeaderRowCells = (headerRow?: Row<T>) => {
    // The table's own header row, in platter mode, floats on the contrast
    // surface between the platters. A per-section header row (`headerRow`)
    // renders inside its platter instead, so it keeps the default surface its
    // rows have.
    const platterHeader = Boolean(cardRows) && !headerRow
    const { ids: scopedIds, togglableIds } = selectable
      ? selectionScopeIn(
          headerRow
            ? rows.filter((row) => row.parentId === headerRow.parentId)
            : rows,
        )
      : { ids: [], togglableIds: [] }
    const { allSelected, someSelected } = selectionStateOf(scopedIds)
    // A select-all that could only ever partly apply — some row in its scope is
    // blocked and still unselected — is offered as disabled rather than as a
    // control that silently does less than it says.
    const selectAllDisabled = togglableIds.length < scopedIds.length
    return (
      <>
        {selectable && (
          <SelectAllHeaderCell
            allSelected={allSelected}
            someSelected={someSelected}
            disabled={selectAllDisabled}
            showControl={showSelectAll !== false && scopedIds.length > 0}
            onToggle={() => toggleIds(togglableIds, allSelected)}
            sticky={selectSticky}
            divider={selectDivider}
            edgeGutterPx={edgeGutterPx}
            platterHeader={platterHeader}
          />
        )}
        {displayColumns.map((leaf) => {
          const header = leafHeaderById.get(leaf.id)
          if (!header) {
            return null
          }
          return (
            <HeaderCell
              key={header.id}
              header={header}
              resizeLabel={columnLabel(
                columnsById.get(leaf.id) ?? { id: leaf.id, header: leaf.id },
              )}
              align={columnAlign(columnsById.get(leaf.id))}
              pin={pinnedById.get(leaf.id)}
              scrolledX={scrolledX}
              canScrollRight={canScrollRight}
              platterHeader={platterHeader}
            />
          )
        })}
        {hasActions && (
          <ActionsHeaderCell
            sticky
            edgeDivider={actionsEdgeDivider}
            edgeGutterPx={edgeGutterPx}
            platterHeader={platterHeader}
          >
            {headerRow ? null : headerAction}
          </ActionsHeaderCell>
        )}
      </>
    )
  }

  // Group bands vs. platter rows: the consumer's `renderGroupRow` decides (it
  // returns nothing for a data row), and in platter mode the two render into
  // different parents, so the split is needed before laying anything out.
  const isGroupRow = (row: Row<T>) => renderGroupRow?.(row.original) != null
  const isGroupRowAt = (index: number) => {
    const row = rows[index]
    return row ? isGroupRow(row) : false
  }

  // One row of the virtualized window. `offsetY` is the top of the box the row
  // is positioned in — a platter's own top, or 0 out in the table flow.
  const renderVirtualRow = (virtualItem: VirtualItem, offsetY: number) => {
    const row = rows[virtualItem.index]
    if (!row) {
      return null
    }
    const top = virtualItem.start - offsetY
    // Bottom row of its platter: the next row opens a new section, or the list
    // ends. Its divider is dropped — the platter's own frame closes it off.
    const platterLast =
      virtualItem.index === rows.length - 1 ||
      isGroupRowAt(virtualItem.index + 1)
    // Injected column-header rows (`perSectionHeaders`) render the same sortable
    // header cells as the global header, aligned to the grid, in the virtualized
    // flow — non-interactive, standing in for it.
    if (isColumnHeaderRow?.(row.original)) {
      return (
        <div
          key={row.id}
          role="row"
          aria-rowindex={headerRowCount + virtualItem.index + 1}
          className={cn(
            "group absolute left-0 top-0 grid items-center",
            // Platter mode: the platter paints the surface and the frame, so the
            // header only carries the divider under its cells.
            cardRows
              ? !platterLast && "border-b border-coval-border"
              : "border-b border-coval-border bg-surface-default",
            EDGE_GUTTER_CLASS,
            !cardRows && FLUSH_EDGE_CELLS_CLASS,
            rowWidthClass,
          )}
          style={{
            gridTemplateColumns,
            height: virtualItem.size,
            transform: `translateY(${top}px)`,
          }}
        >
          {renderHeaderRowCells(row)}
        </div>
      )
    }
    return (
      <BodyRow
        key={row.id}
        row={row}
        columnsById={columnsById}
        displayColumns={displayColumns}
        pinnedById={pinnedById}
        gridTemplateColumns={gridTemplateColumns}
        rowWidthClass={rowWidthClass}
        onRowClick={onRowClick}
        isRowClickable={isRowClickable}
        rowHover={rowHover}
        active={row.id === activeRowId}
        onPointerEnter={onRowPointerEnter}
        onPointerLeave={onRowPointerLeave}
        selectable={selectable}
        rowSelectable={!isRowSelectable || isRowSelectable(row.original)}
        rowSelectionDisabled={Boolean(isRowSelectionDisabled?.(row.original))}
        selectionAlwaysVisible={selectionVisibility === "always"}
        rowClickSelects={Boolean(rowClickSelects)}
        selectSticky={selectSticky}
        selectDivider={selectDivider}
        scrolledX={scrolledX}
        canScrollRight={canScrollRight}
        actionsSticky
        actionsEdgeDivider={actionsEdgeDivider}
        selected={selectedSet.has(row.id)}
        variant={rowVariant?.(row.original)}
        cellVariant={cellVariant}
        onToggleSelect={toggleRow}
        rowActions={rowActions}
        rowCopyId={rowCopyId}
        rowHref={rowHref}
        onRowOpenInNewTab={onRowOpenInNewTab}
        hasActions={hasActions}
        nestable={nestable}
        firstColumnId={firstColumnId}
        renderGroupRow={renderGroupRow}
        start={top}
        size={virtualItem.size}
        index={virtualItem.index}
        measured={measureRow ? measureRow(row.original) : false}
        measureElement={virtualizer.measureElement}
        rowAttributes={rowAttributes ? rowAttributes(row.original) : undefined}
        unreadIndicator={Boolean(isRowUnread)}
        unread={isRowUnread ? isRowUnread(row.original) : false}
        edgeGutterPx={edgeGutterPx}
        cardRows={Boolean(cardRows)}
        platterLast={platterLast}
        rowLabel={getRowLabel?.(row.original)}
        labelColumnId={labelColumnId}
        controlNamesId={controlNamesId}
        ariaRowIndex={headerRowCount + virtualItem.index + 1}
        columnCount={columnCount}
        clickDescriptionId={clickDescriptionId}
      />
    )
  }

  // Platter sections and their extents. Measurements cover every row (not just
  // the rendered window), so a platter spans its whole section even when only
  // part of it is on screen — its frame can't end mid-scroll. The cache is the
  // public read of what `getVirtualItems()` above just computed.
  const platterSections = cardRows ? buildPlatterSections(rows, isGroupRow) : []
  const measurements = cardRows ? virtualizer.measurementsCache : []
  const firstVisibleIndex = virtualItems[0]?.index ?? -1
  const lastVisibleIndex = virtualItems[virtualItems.length - 1]?.index ?? -1
  const visiblePlatterSections = platterSections.filter(
    (section) =>
      section.firstIndex <= lastVisibleIndex &&
      section.lastIndex >= firstVisibleIndex,
  )

  // A platter: the frame stays put while its own horizontal scroll region moves
  // the columns inside it, so content can never run past the frame and a pinned
  // column sticks to the frame's edge. `role="presentation"` keeps the box out of
  // the a11y tree so its rows stay children of the rowgroup.
  const renderPlatter = (section: PlatterSection) => {
    const start = measurements[section.firstIndex]?.start ?? 0
    const end = measurements[section.lastIndex]?.end ?? start
    const height = end - start
    // Card rows carry their nesting indent on the platter (inset + narrowed to
    // match), so its left edge aligns under the group icon.
    const depth = rows[section.firstIndex]?.depth ?? 0
    const inset =
      nestable && depth > 0 ? edgeGutterPx + depth * cardNestLead : 0
    return (
      <div
        key={section.key}
        role="presentation"
        ref={registerPlatter(section.key)}
        onScroll={handlePlatterScroll}
        // The scrollbar is hidden because a classic (non-overlay) one would eat
        // into the height the virtualizer sized this box to and clip the bottom
        // row; the pinned column and its edge divider carry the affordance.
        className="absolute overflow-x-auto overflow-y-hidden rounded-md border border-coval-border bg-surface-default [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{
          top: start,
          height,
          left: inset,
          width: `calc(100% - ${inset}px)`,
        }}
      >
        <div role="presentation" style={{ position: "relative", height }}>
          {virtualItems
            .filter(
              (virtualItem) =>
                virtualItem.index >= section.firstIndex &&
                virtualItem.index <= section.lastIndex,
            )
            .map((virtualItem) => renderVirtualRow(virtualItem, start))}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={scrollRef}
      role="table"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      aria-rowcount={ariaRowCount}
      aria-busy={isLoading || isFetchingMore ? true : undefined}
      onScroll={handleScroll}
      // `--table-edge-gutter` drives the outer padding (EDGE_GUTTER_CLASS) and
      // the pinned-column gutter covers; `edgeToEdge` collapses it to zero.
      style={
        { "--table-edge-gutter": `${edgeGutterPx}px` } as React.CSSProperties
      }
      className={cn(
        "relative overflow-auto text-sm text-coval-text",
        ringRoom ? "-mx-[2px] w-[calc(100%+4px)]" : "w-full",
        // Frames a framed table against the shell. Only the frame's paint goes
        // on this element -- it is the table's scroll container, so it keeps the
        // `overflow-auto` above rather than the frame's `overflow-hidden`.
        framed && TABLE_FRAME_SURFACE_CLASS,
        // Empty: the header keeps its natural height and the body takes the rest,
        // which is what lets the empty state center against the frame rather
        // than sit directly under the header.
        showEmpty && "flex flex-col",
        // Card rows add a small gutter so platters don't sit flush to the edge;
        // the canvas stays transparent so it inherits the page background.
        cardRows && "px-3 pt-3",
        className,
      )}
    >
      {/* Header — a single sticky row at the top, unless `perSectionHeaders`
          moves the headers into consumer-injected section-header rows. */}
      {!perSectionHeaders && (
        <div role="rowgroup" className="sticky top-0 z-10">
          {table.getHeaderGroups().map((headerGroup, headerIndex) => (
            <div
              key={headerGroup.id}
              role="row"
              aria-rowindex={headerIndex + 1}
              // Width tracks the body rows (see `rowWidthClass`) so pinned columns
              // line up during horizontal scroll and the bottom border spans every
              // column.
              className={cn(
                "grid",
                // Card mode: header floats on the contrast surface, no divider.
                cardRows
                  ? "bg-surface-contrast"
                  : "border-b border-coval-border bg-surface-default",
                EDGE_GUTTER_CLASS,
                !cardRows && FLUSH_EDGE_CELLS_CLASS,
                rowWidthClass,
              )}
              style={{ gridTemplateColumns }}
            >
              {renderHeaderRowCells()}
            </div>
          ))}
        </div>
      )}

      {/* Body */}
      <div
        role="rowgroup"
        // A row's right-click menu is mounted on the row itself, so anything
        // inside the row that owns its own menu has to be excluded here, before
        // the event reaches it. See `shouldOpenRowContextMenu`.
        onContextMenuCapture={(event) => {
          if (!shouldOpenRowContextMenu(event.target)) {
            event.stopPropagation()
          }
        }}
        // Platter mode: the skeleton rows paint their own frame (there are no
        // sections to build platters from yet), so clip them here — otherwise
        // columns wider than the container would scroll the whole table until
        // the data arrives.
        className={cn(
          isLoading && cardRows && "overflow-hidden",
          showEmpty && "flex min-h-0 flex-1 items-center justify-center",
        )}
      >
        {isLoading ? (
          <LoadingRows
            displayColumns={displayColumns}
            pinnedById={pinnedById}
            gridTemplateColumns={gridTemplateColumns}
            rowWidthClass={rowWidthClass}
            selectable={selectable}
            selectSticky={selectSticky}
            selectDivider={selectDivider}
            scrolledX={scrolledX}
            canScrollRight={canScrollRight}
            hasActions={hasActions}
            edgeGutterPx={edgeGutterPx}
            cardRows={Boolean(cardRows)}
            columnCount={columnCount}
            ariaRowIndex={headerRowCount + 1}
          />
        ) : showEmpty ? (
          <FullWidthRow
            columnCount={columnCount}
            ariaRowIndex={headerRowCount + 1}
            // The empty row pads by the table's own gutter, like every other
            // row, so a table that empties out doesn't shift its content inset.
            className={cn(
              "w-full py-10 text-center text-coval-text-muted",
              EDGE_GUTTER_CLASS,
            )}
          >
            {emptyState ?? "No results"}
          </FullWidthRow>
        ) : (
          <div
            style={{ height: virtualizer.getTotalSize(), position: "relative" }}
          >
            {cardRows
              ? // Platter mode: the rows of a section render inside their own
                // platter (see `renderPlatter`), which leaves only the group
                // bands in this flow. Both are positioned off the same
                // virtualizer offsets, so they stay interleaved on screen; the
                // DOM follows row order too, so a screen reader meets rows in
                // the order their `aria-rowindex` gives.
                [
                  ...virtualItems
                    .filter((virtualItem) => isGroupRowAt(virtualItem.index))
                    .map((virtualItem) => ({
                      index: virtualItem.index,
                      node: renderVirtualRow(virtualItem, 0),
                    })),
                  ...visiblePlatterSections.map((section) => ({
                    index: section.firstIndex,
                    node: renderPlatter(section),
                  })),
                ]
                  .sort((a, b) => a.index - b.index)
                  .map(({ node }) => node)
              : virtualItems.map((virtualItem) =>
                  renderVirtualRow(virtualItem, 0),
                )}
          </div>
        )}
        {isFetchingMore && !isLoading && (
          <FullWidthRow
            columnCount={columnCount}
            ariaRowIndex={headerRowCount + rows.length + 1}
            cellClassName="flex justify-center py-3"
          >
            <span role="status" className="sr-only">
              Loading more rows
            </span>
            <Skeleton variant="text" className="w-16" />
          </FullWidthRow>
        )}
      </div>
      {/* Referenced by every clickable row's `aria-describedby`; `hidden` keeps
          it out of the table's own structure while still lending its text. */}
      <span id={clickDescriptionId} hidden>
        {rowClickDescription ??
          (rowClickSelects ? "Press Enter to select" : "Press Enter to open")}
      </span>
      <span hidden>
        {Object.entries(ROW_CONTROL_WORDS).map(([kind, words]) => (
          <span key={kind} id={`${controlNamesId}-${kind}`}>
            {words}:
          </span>
        ))}
      </span>
    </div>
  )
}
